// @ai-generated 2026-08-28T07:59:23+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
import { getDynamicExportTypeLabel, getStateLabelAndVariant } from '@Constants/dynamicExportConstants';
import { type ApiDynamicExportRun, EnumDynamicExportRunState } from '@Model';
import { renderDateTime } from '@synqo/frontend-component-library';
import { exportRunResultLines, exportRunResultMessages } from '@utils/exportRunResult';
import { getPrimaryTraceId } from '@utils/traceIdUtils';
import { type MessageVariant } from '../Messages/messageVariant';
import { formatElapsed } from './exportRunTimeline';

/** How a run turned out, in the words and the tone a reader gets before reading anything else. */
export interface ExportRunOutcome {
    variant: MessageVariant;
    headline: string;
    detail: string;
}

/**
 * The run's own verdict on itself. The state alone is not the verdict: a run that finished without
 * producing a file has nothing to download and is not the success its state claims, and a failed
 * run is only useful once it says why.
 */
export function describeExportRunOutcome(run: ApiDynamicExportRun): ExportRunOutcome {
    switch (run.state) {
        case EnumDynamicExportRunState.Scheduled:
            return {
                variant: 'info',
                headline: 'Export eingeplant',
                detail: 'Der Lauf wartet auf einen freien Verarbeitungsplatz.',
            };
        case EnumDynamicExportRunState.Running:
            return {
                variant: 'info',
                headline: 'Export läuft',
                detail: `Seit ${renderDateTime(run.modified_at, 'datetime')} in Bearbeitung.`,
            };
        case EnumDynamicExportRunState.Finished:
            return finishedOutcome(run);
        case EnumDynamicExportRunState.Failed:
            return { variant: 'error', headline: 'Export fehlgeschlagen', detail: describeFailureReason(run) };
        default:
            return {
                variant: 'info',
                headline: 'Unbekannter Status',
                detail: 'Der Lauf meldet einen Status, den diese Oberfläche nicht kennt.',
            };
    }
}

/**
 * The run as the plain text a user pastes into a support ticket: what it was, when it ran, every
 * message it reported and the reference support needs to find the run in the logs.
 */
export function buildExportRunReport(run: ApiDynamicExportRun): string {
    const elapsed = formatElapsed(run.created_at, run.modified_at);
    const traceId = getPrimaryTraceId(exportRunResultMessages(run));

    const lines = [
        `Export ${run.number} — ${getStateLabelAndVariant(run.state).label}`,
        getDynamicExportTypeLabel(run.category, run.key),
        `Eingeplant: ${renderDateTime(run.created_at, 'datetime')}`,
        `Letzte Änderung: ${renderDateTime(run.modified_at, 'datetime')}${elapsed ? ` (nach ${elapsed})` : ''}`,
        '',
        ...exportRunResultLines(run).map((line) => `- ${line}`),
    ];

    if (traceId) {
        lines.push('', `Support-Referenz: ${traceId}`);
    }

    return lines.join('\n');
}

function finishedOutcome(run: ApiDynamicExportRun): ExportRunOutcome {
    if (run.result_file_id) {
        return {
            variant: 'success',
            headline: 'Export abgeschlossen',
            detail: 'Die Exportdatei steht zum Download bereit.',
        };
    }

    return {
        variant: 'warning',
        headline: 'Export abgeschlossen, ohne Datei',
        detail: 'Der Lauf ist durchgelaufen, hat aber keine Exportdatei erzeugt.',
    };
}

/**
 * Why the run failed, in its own words. The backend puts the message that aborted the pipeline
 * first, so that one is the answer; how many followed it is the message list's business.
 */
function describeFailureReason(run: ApiDynamicExportRun): string {
    const [first] = exportRunResultMessages(run);
    return first?.message || 'Es wurde kein Grund übermittelt. Bitte wenden Sie sich an den Support.';
}
